import { Box, Grid, Stack, Typography } from '@mui/material'
import { FeatureFlagIdGrantReasonsManualMembership } from 'c1gen/features'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import List from 'components/core/list/index'
import { SelectOneAccessProfileHook } from 'components/core/search/catalogs'
import { SelectOneUserHook } from 'components/core/search/users'
import { RelativeTimestamp } from 'components/core/time/display'
import { useErrorReporting } from 'components/no-ui/error-reporting/datadog'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { QUERIES } from 'components/pages/explore/query/Query'
import { StructuredSearchStyles } from 'components/pages/explore/query/styles'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { RequestCatalogValue } from 'lib/actions/request_catalog'
import { UserValue } from 'lib/actions/user'
import { GrantReasonDerivedType } from 'lib/constants/system'
import { useInitialFilters } from 'lib/search'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { AccessProfileMembershipRequest, RequestCatalog } from 'pbts/c1/api/requestcatalog/v1/requestcatalog'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { AccessProfileMembershipValue, searchAccessProfileMembershipsApi } from 'redux_toolkit/request-catalogs'
import { ExportData } from './ExportData'

const cols: C1TableReduxColumn<AccessProfileMembershipValue>[] = [
    {
        field: 'accessProfile',
        flex: 2,
        label: <FormattedMessage id="access_profile_membership_access_profile_name" defaultMessage="Access Profile" />,
        Component: ({ row }) => (
            row.accessProfile
                ? <C1EntitlementDisplayName
                    entitlement={row.accessProfile}
                />
                : <C1EmptyIndicator />
        ),
    },
    {
        field: 'user',
        flex: 2,
        label: <FormattedMessage id="access_profile_membership_user" defaultMessage="User" />,
        Component: ({ row }) => (
            row.user
                ? <UserDisplayName user={row.user} showTooltip showUserStatus />
                : <Typography variant="unconfigured">
                    <FormattedMessage id="access_profile_membership_no_user" defaultMessage="Not assigned" />
                </Typography>
        ),
    },
    {
        field: 'addedAt',
        label: <FormattedMessage id="access_profile_membership_added_on" defaultMessage="Date added" />,
        Component: ({ row }) => (
            row.addedAt
                ? <RelativeTimestamp
                    date={Timestamp.toDate(row.addedAt)}
                    variant="dense"
                    typographyVariant="body1"
                />
                : <C1EmptyIndicator />
        ),
    },
    {
        field: 'addedVia',
        label: <FormattedMessage id="access_profile_membership_added_via" defaultMessage="Added via" />,
        Component: ({ row }): React.ReactElement => {
            let isAutomated = false
            let isManual = false

            row.addedVia?.forEach((reason) => {
                switch (reason.derivedIdType) {
                    case GrantReasonDerivedType.GrantReasonCatalogAutomation:
                        isAutomated = true
                        break
                    case GrantReasonDerivedType.GrantReasonCatalogManual:
                        isManual = true
                        break
                    default:
                        break
                }
            })

            return (!isAutomated && !isManual)
                ? <C1EmptyIndicator />
                : <Stack spacing={1}>
                    {isAutomated && <Typography>
                        <FormattedMessage id="components_app_entitlement_user_list_added_via_automated" defaultMessage="Membership automation" />
                    </Typography>}
                    {isManual && <Typography>
                        <FormattedMessage id="components_app_entitlement_user_list_added_via_manual" defaultMessage="Manual" />
                    </Typography>}
                </Stack>
        },
        shouldRender: (hasFeature): boolean =>
            hasFeature(FeatureFlagIdGrantReasonsManualMembership) || false,

    },
]

export const AccessProfileMembershipReport: FC = () => {
    const [page, setPage] = useState<number>(0)

    const initialFilter = useInitialFilters(AccessProfileMembershipRequest)
    const [filter, setFilter] = useState(AccessProfileMembershipRequest.create({ ...initialFilter }))

    const onChange = useCallback((updates: Partial<AccessProfileMembershipRequest>) => {
        setFilter((prev) => AccessProfileMembershipRequest.create({
            ...prev,
            ...updates,
        }))
    }, [])

    const {
        data,
        isError,
        isLoading,
        getPage,
        canLoadMore,
        error,
    } = searchAccessProfileMembershipsApi.useSearchAccessProfileMemberships(
        AccessProfileMembershipRequest.create({ ...filter }),
        {},
    )

    const onPageChange = useCallback(
        async (n: number): Promise<void> => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    useErrorReporting(isError, error, QUERIES.AccessProfileMembership)

    const onSelectedCatalog = (catalog: RequestCatalogValue | null): void =>
        onChange({ accessProfileId: catalog?.id || '' })

    const onSelectedUser = (user: UserValue | null): void =>
        onChange({ userId: user?.id || '' })

    // Create request for downloading the report
    const exportDataActionRequest = useMemo(() => {
        return ExportDataRequest.create({
            queryId: QueryId.ACCESS_PROFILE_MEMBERSHIP,
            request: {
                oneofKind: 'accessProfileMembershipRequest',
                accessProfileMembershipRequest: AccessProfileMembershipRequest.create({
                    ...filter,
                }),
            },
        })
    }, [filter])

    const userTable = useMemo(
        () => (
            <C1TableRedux
                data={data}
                cols={cols}
                canLoadMore={canLoadMore}
                isError={isError}
                isLoading={isLoading}
                onPageChange={onPageChange}
                error={error}
                page={page}
            />
        ),
        [canLoadMore, data, error, isError, isLoading, onPageChange, page],
    )

    return <>
        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
            spacing={2}
            sx={StructuredSearchStyles.filterInputs}
        >
            <Grid item sm={12} md={4} key="select-catalog-container">
                <SelectOneAccessProfileHook
                    onSelected={onSelectedCatalog}
                    type={RequestCatalog}
                />
            </Grid>
            <Grid item sm={12} md={4} key="select-user-container">
                <SelectOneUserHook onSelected={onSelectedUser} />
            </Grid>
            <Grid item sm={12} md={4} key="export-data-container" sx={{ height: { md: '100%' } }}>
                <Box sx={StructuredSearchStyles.downloadReportContainer}>
                    <ExportData exportDataRequest={exportDataActionRequest} />
                </Box>
            </Grid>
        </Grid>

        <List<AccessProfileMembershipValue> table={userTable} />
    </>
}
